import type {
  ProductSearchFilterCategory,
  ProductSearchFilterTypeKeys,
  ProductSearchFiltersViewModel,
  SearchFilterFacet,
  SliderFilterKeys,
} from '@jane/bloom/viewModels/productSearchFilters';

import { useBloomFilters } from './useBloomFilters';

export const FilterCategoryLabel: Record<
  keyof ProductSearchFiltersViewModel | 'potencies',
  string
> = {
  activities: 'Activity',
  brands: 'Brand',
  brandSpecials: 'Jane Gold',
  categories: 'Category',
  cbdPotencies: 'CBD potency',
  feelings: 'Feeling',
  lineages: 'Lineage',
  potencies: 'Potency',
  prices: 'Price',
  ratings: 'Rating',
  specials: 'Deal',
  terpenes: 'Top terpenes',
  thcPotencies: 'THC potency',
  weights: 'Weight',
};

export function useProductSearchFilters() {
  const { availableFilters, selectedCategory } = useBloomFilters();

  const getProductSearchFilterOptions = (
    filter: keyof ProductSearchFiltersViewModel | 'ratings'
  ) => {
    return availableFilters?.[filter] || [];
  };

  const getProductSubcategories = (selectedCategory: string) => {
    const cat = availableFilters?.['categories'].find(
      (category) => category.value === selectedCategory
    )?.subCategories;

    return cat || [];
  };

  const getBooleanFilterKeysNonEmpty = () => {
    return Object.entries(availableFilters || {})
      .filter(([key, val]) => {
        return !key.toLowerCase().includes('potencies') && val.length > 0;
      })
      .map(([key, _]) => key as ProductSearchFilterTypeKeys);
  };

  const getPotencyFilterKeysNonEmpty = () => {
    return Object.entries(availableFilters || {})
      .filter(([key, val]) => {
        return key.toLowerCase().includes('potencies') && val.length > 0;
      })
      .map(([key, _]) => key as SliderFilterKeys);
  };

  const getProductSearchFilterOptionLabel = (
    filter: keyof ProductSearchFiltersViewModel,
    filterOption: string
  ) => {
    const category = availableFilters?.[filter] as SearchFilterFacet[];

    if (filter === 'categories') {
      const [rootFilterOption, subFilterOption] = filterOption.split(':');

      if (subFilterOption) {
        const item = (category || []).find(
          (option) => option.value === rootFilterOption
        ) as ProductSearchFilterCategory;

        return item?.subCategories?.find((s) => s.value === filterOption)
          ?.label;
      }
    }

    return (
      (category || []).find((option) => option.value === filterOption)?.label ||
      filterOption
    );
  };

  return {
    getBooleanFilterKeysNonEmpty,
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptionLabel,
    getProductSearchFilterOptions,
    getProductSubcategories,
    productSearchFilters: availableFilters,
    selectedCategory,
  };
}
